import {Injectable, NgZone} from '@angular/core';
import {Subject} from 'rxjs';
import {OverlayElement} from '../api/interfaces/overlay-element.interface';
import {OverlayElementApiService} from '../api/services/overlay-element.service';
import {OverlayElementStateData} from '../../../../maporium/src/app/entities/types/overlay-element-state-data';
import Konva from 'konva';

export interface OverlayElementEvent {
  type: 'select' | 'deselect' | 'move' | 'transform' | 'create' | 'delete';
  element?: OverlayElement;
  before?: Partial<OverlayElement>;
  after?: Partial<OverlayElement>;
}

@Injectable({providedIn: 'root'})
export class OverlayElementManagerService {
  private stage: Konva.Stage | null = null;
  private layer: Konva.Layer | null = null;
  private transformer: Konva.Transformer | null = null;
  private cy: any = null;
  private containerDiv: HTMLDivElement | null = null;
  private elements: Map<string, OverlayElement> = new Map();
  private shapes: Map<string, Konva.Rect> = new Map();
  private selectedElementId: string | null = null;
  private currentStateId: string | null = null;
  private enabled = false;
  private initialized = false;
  private interacting = false;
  private lastPanningEnabled = true;
  private lastZoomingEnabled = true;
  private lastBoxSelectionEnabled = true;

  public events$ = new Subject<OverlayElementEvent>();

  constructor(
    private overlayElementService: OverlayElementApiService,
    private ngZone: NgZone
  ) {
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getSelectedElement(): OverlayElement | null {
    if (!this.selectedElementId) return null;
    const element = this.elements.get(this.selectedElementId);
    if (!element) return null;
    return this.getEffectiveElement(element);
  }

  getElements(): OverlayElement[] {
    return Array.from(this.elements.values());
  }

  getCurrentStateId(): string | null {
    return this.currentStateId;
  }

  initialize(cy: any): void {
    // Feature flag check
    if (!localStorage.getItem('ENABLE_RECTANGLE_FEATURE')) {
      this.enabled = false;
      return;
    }

    this.enabled = true;
    this.cy = cy;

    if (this.initialized) {
      this.syncAllShapes();
      return;
    }

    const container = cy.container();
    if (!container) return;

    // Create a separate div for our Konva stage
    this.containerDiv = document.createElement('div');
    this.containerDiv.id = 'overlay-elements-stage-container';
    this.containerDiv.style.position = 'absolute';
    this.containerDiv.style.top = '0';
    this.containerDiv.style.left = '0';
    this.containerDiv.style.width = '100%';
    this.containerDiv.style.height = '100%';
    this.containerDiv.style.zIndex = '1001';
    container.appendChild(this.containerDiv);

    this.stage = new Konva.Stage({
      container: this.containerDiv,
      width: container.clientWidth,
      height: container.clientHeight
    });

    this.layer = new Konva.Layer();
    this.stage.add(this.layer);

    // Transformer for resize+rotate
    this.transformer = new Konva.Transformer({
      rotateEnabled: true,
      enabledAnchors: [
        'top-left', 'top-right', 'bottom-left', 'bottom-right',
        'middle-left', 'middle-right', 'top-center', 'bottom-center'
      ],
      boundBoxFunc: (oldBox, newBox) => {
        if (newBox.width < 10 || newBox.height < 10) {
          return oldBox;
        }
        return newBox;
      }
    });
    this.layer.add(this.transformer);

    // Sync with Cytoscape pan/zoom
    cy.on('render zoom pan', () => {
      this.syncAllShapes();
    });

    // Click on empty stage area = deselect
    this.stage.on('click tap', (e: any) => {
      if (e.target === this.stage) {
        this.deselectAll();
      }
    });

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      if (this.stage && this.containerDiv) {
        this.stage.width(this.containerDiv.clientWidth);
        this.stage.height(this.containerDiv.clientHeight);
        this.syncAllShapes();
      }
    });
    resizeObserver.observe(container);

    this.initialized = true;
  }

  destroy(): void {
    this.resetGestures();

    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
    if (this.containerDiv?.parentNode) {
      this.containerDiv.parentNode.removeChild(this.containerDiv);
      this.containerDiv = null;
    }
    this.layer = null;
    this.transformer = null;
    this.elements.clear();
    this.shapes.clear();
    this.selectedElementId = null;
    this.currentStateId = null;
    this.initialized = false;
  }

  loadElements(elements: OverlayElement[]): void {
    if (!this.enabled) return;

    // Clear existing shapes
    this.shapes.forEach(shape => shape.destroy());
    this.shapes.clear();
    this.elements.clear();

    elements.forEach(el => {
      this.elements.set(el.id!, el);
      this.addShape(el);
    });

    this.syncAllShapes();
  }

  addElement(element: OverlayElement): void {
    if (!this.enabled) return;

    this.elements.set(element.id!, element);
    this.addShape(element);
    this.syncAllShapes();
  }

  /**
   * @param optimistic When true, updates the local state entry instead of base data
   *                   so that getEffectiveElement returns the correct values during syncAllShapes.
   *                   Use true for form-driven changes, false for server responses.
   */
  updateElement(element: OverlayElement, optimistic = false): void {
    if (!this.enabled) return;

    const existing = this.elements.get(element.id!);
    if (existing) {
      if (optimistic && this.currentStateId && !existing.excludedFromState) {
        // Optimistic update in non-base state: update the local state entry
        // so getEffectiveElement returns the new values during syncAllShapes.
        if (!existing.states) existing.states = [];
        let stateEntry = existing.states.find(
          (s: OverlayElementStateData) => s.stateId === this.currentStateId
        );
        if (!stateEntry) {
          stateEntry = {stateId: this.currentStateId, element: {} as any};
          existing.states.push(stateEntry);
        }
        Object.assign(stateEntry.element, {
          id: element.id,
          name: element.name,
          description: element.description,
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
          rotation: element.rotation,
          type: element.type,
          imageUrl: element.imageUrl,
          properties: element.properties ? {...element.properties} : stateEntry.element.properties,
          excludedFromState: element.excludedFromState,
        });
      } else {
        // Server response or base state: replace entire element data
        Object.assign(existing, element);
      }

      const shape = this.shapes.get(element.id!);
      if (shape) {
        this.applyElementToShape(shape, this.getEffectiveElement(existing));
        this.layer?.batchDraw();
      }
    }
  }

  removeElement(elementId: string): void {
    if (!this.enabled) return;

    const shape = this.shapes.get(elementId);
    if (shape) {
      shape.destroy();
      this.shapes.delete(elementId);
    }
    this.elements.delete(elementId);
    if (this.selectedElementId === elementId) {
      this.selectedElementId = null;
      this.transformer?.nodes([]);
    }
    this.layer?.batchDraw();
  }

  selectElement(elementId: string): void {
    if (!this.enabled) return;

    // Disable drag on previously selected shape
    if (this.selectedElementId && this.selectedElementId !== elementId) {
      const prevShape = this.shapes.get(this.selectedElementId);
      if (prevShape) prevShape.draggable(false);
    }

    this.selectedElementId = elementId;
    const shape = this.shapes.get(elementId);
    if (shape && this.transformer) {
      this.transformer.nodes([shape]);
      // Keep shape non-draggable on select — dragging is enabled
      // in the mousedown handler only when the element is already selected
      // (click-hold to drag pattern).
      shape.draggable(false);
      this.layer?.batchDraw();
    }

    const element = this.elements.get(elementId);
    if (element) {
      this.events$.next({type: 'select', element: this.getEffectiveElement(element)});
    }
  }

  deselectAll(): void {
    if (!this.enabled) return;

    if (this.selectedElementId) {
      const prevShape = this.shapes.get(this.selectedElementId);
      if (prevShape) prevShape.draggable(false);
      this.selectedElementId = null;
      this.transformer?.nodes([]);
      this.layer?.batchDraw();
      this.events$.next({type: 'deselect'});
    }
  }

  /**
   * Adds local state entries for all overlay elements so that switchState
   * can find them immediately without a server re-fetch.
   */
  populateStateEntries(newStateId: string, sourceStateId: string | null): void {
    this.elements.forEach((element) => {
      if (element.excludedFromState) return;
      if (!element.states) element.states = [];

      let newElementData: any;
      if (sourceStateId) {
        const sourceEntry = element.states.find((s: any) => s.stateId === sourceStateId);
        if (sourceEntry) {
          newElementData = JSON.parse(JSON.stringify(sourceEntry.element));
        }
      }
      if (!newElementData) {
        const effective = this.getEffectiveElement(element);
        newElementData = {
          id: effective.id,
          name: effective.name,
          description: effective.description,
          x: effective.x,
          y: effective.y,
          width: effective.width,
          height: effective.height,
          rotation: effective.rotation,
          type: effective.type,
          imageUrl: effective.imageUrl,
          properties: effective.properties ? JSON.parse(JSON.stringify(effective.properties)) : undefined,
        };
      }

      element.states.push({stateId: newStateId, element: newElementData});
    });
  }

  switchState(stateId: string | null): void {
    if (!this.enabled) return;

    // Deselect any selected element when switching states
    this.deselectAll();

    this.currentStateId = stateId;

    // Re-sync all shapes using state-aware rendering
    this.elements.forEach((element, id) => {
      if (element.excludedFromState) return;
      this.syncShape(id);
    });

    this.layer?.batchDraw();
  }

  getStageCanvas(): HTMLCanvasElement | null {
    if (!this.stage) return null;
    return this.stage.toCanvas({
      pixelRatio: 2
    }) as HTMLCanvasElement;
  }

  // --- Private methods ---

  private addShape(element: OverlayElement): void {
    if (!this.layer) return;

    const props = element.properties || {};
    const shape = new Konva.Rect({
      id: element.id,
      x: 0,
      y: 0,
      width: element.width || 100,
      height: element.height || 100,
      fill: props.backgroundColor || '#d3d3d3',
      stroke: props.borderColor || '#000000',
      strokeWidth: props.borderWidth ?? 2,
      opacity: props.opacity ?? 1,
      rotation: (element.rotation || 0) * (180 / Math.PI),
      draggable: false,
      dash: this.getDashArray(props.borderStyle),
    });

    // Cursor feedback on hover
    shape.on('mouseenter', () => {
      const stageContainer = this.stage?.container();
      if (stageContainer) {
        stageContainer.style.cursor = 'move';
      }
    });

    shape.on('mouseleave', () => {
      const stageContainer = this.stage?.container();
      if (stageContainer) {
        stageContainer.style.cursor = 'default';
      }
    });

    // Prevent Cy from receiving mousedown on shapes.
    // If the element is already selected, enable draggable so a
    // click-hold+move starts a drag. Konva checks draggable on
    // subsequent mousemove, so setting it here works for hold-to-drag.
    shape.on('mousedown touchstart', (e: any) => {
      e.evt?.stopPropagation();

      // Enable drag only when the element is already selected
      if (this.selectedElementId === element.id) {
        shape.draggable(true);
      }
    });

    // Click to select (click = mousedown + mouseup without drag)
    shape.on('click tap', (e: any) => {
      e.cancelBubble = true;
      e.evt?.stopPropagation();
      // Reset draggable after a plain click (no drag occurred)
      shape.draggable(false);
      this.ngZone.run(() => {
        this.selectElement(element.id!);
      });
    });

    // Drag move - store before position at start
    let dragBeforeModel: { x: number; y: number } | null = null;

    shape.on('dragstart', () => {
      this.interacting = true;
      this.disableGestures();
      const effective = this.getEffectiveElement(element);
      dragBeforeModel = {x: effective.x || 0, y: effective.y || 0};
    });

    shape.on('dragend', () => {
      this.interacting = false;
      this.resetGestures();
      // Reset draggable after drag completes — next drag requires a new mousedown
      shape.draggable(false);
      const afterModel = this.toModel(shape.x(), shape.y());
      const before = dragBeforeModel || {x: element.x || 0, y: element.y || 0};

      // State-aware local update
      this.updateLocalProperties(element.id!, {x: afterModel.x, y: afterModel.y});

      this.ngZone.run(() => {
        this.events$.next({
          type: 'move',
          element: this.getEffectiveElement(element),
          before: {x: before.x, y: before.y},
          after: {x: afterModel.x, y: afterModel.y}
        });
      });
    });

    // Transform (resize/rotate)
    let transformBefore: Partial<OverlayElement> | null = null;

    shape.on('transformstart', () => {
      this.interacting = true;
      this.disableGestures();
      const effective = this.getEffectiveElement(element);
      transformBefore = {
        width: effective.width,
        height: effective.height,
        rotation: effective.rotation
      };
    });

    shape.on('transformend', () => {
      this.interacting = false;
      this.resetGestures();
      const newWidth = Math.max(10, shape.width() * shape.scaleX());
      const newHeight = Math.max(10, shape.height() * shape.scaleY());
      const newRotation = shape.rotation() * (Math.PI / 180);

      // Reset scale and apply computed dimensions
      shape.scaleX(1);
      shape.scaleY(1);
      shape.width(newWidth);
      shape.height(newHeight);

      // Convert position back to model
      const modelPos = this.toModel(shape.x(), shape.y());
      const zoom = this.cy?.zoom() || 1;

      const afterProps = {
        width: newWidth / zoom,
        height: newHeight / zoom,
        rotation: newRotation,
        x: modelPos.x,
        y: modelPos.y
      };

      // State-aware local update
      this.updateLocalProperties(element.id!, afterProps);

      this.ngZone.run(() => {
        this.events$.next({
          type: 'transform',
          element: this.getEffectiveElement(element),
          before: transformBefore || {},
          after: afterProps
        });
      });
    });

    this.shapes.set(element.id!, shape);
    this.layer.add(shape);

    // Make sure transformer is on top
    this.transformer?.moveToTop();
  }

  private syncAllShapes(): void {
    if (!this.layer || !this.cy) return;

    this.elements.forEach((element, id) => {
      // Skip the shape being actively dragged/transformed — overwriting its
      // properties mid-interaction causes values (rotation, position, size)
      // to snap back to the stored data before transformend can capture them.
      if (this.interacting && id === this.selectedElementId) return;
      this.syncShape(id);
    });

    this.layer.batchDraw();
  }

  private syncShape(elementId: string): void {
    const shape = this.shapes.get(elementId);
    const element = this.elements.get(elementId);
    if (!shape || !element) return;

    this.applyElementToShape(shape, this.getEffectiveElement(element));
  }

  /**
   * State-aware local property update.
   * In base state: writes to the base element.
   * In non-base state: writes to the local state entry so that
   * getEffectiveElement returns the correct values during syncAllShapes.
   */
  private updateLocalProperties(elementId: string, props: Partial<OverlayElement>): void {
    const element = this.elements.get(elementId);
    if (!element) return;

    if (this.currentStateId && !element.excludedFromState) {
      if (!element.states) element.states = [];
      let stateEntry = element.states.find(
        (s: OverlayElementStateData) => s.stateId === this.currentStateId
      );
      if (!stateEntry) {
        // Clone base element as initial state data (without states/metadata)
        const base = {...element} as any;
        delete base.states;
        delete base.metadata;
        stateEntry = {stateId: this.currentStateId, element: base};
        element.states.push(stateEntry);
      }
      Object.assign(stateEntry.element, props);
    } else {
      Object.assign(element, props);
    }
  }

  /**
   * Returns the effective element data for the current state.
   * In base state (or for excluded elements), returns the base element.
   * In a non-base state with state data, merges state data over the base.
   */
  private getEffectiveElement(element: OverlayElement): OverlayElement {
    if (!this.currentStateId || element.excludedFromState) {
      return element;
    }

    const stateData = element.states?.find(
      (s: OverlayElementStateData) => s.stateId === this.currentStateId
    );

    if (stateData && stateData.element) {
      // Merge only defined values from state data over the base to avoid
      // overriding base properties with undefined (from partial state entries).
      const result = {...element};
      for (const [key, value] of Object.entries(stateData.element)) {
        if (value !== undefined && key !== 'states') {
          (result as any)[key] = value;
        }
      }
      // Deep-merge properties so partial state properties don't wipe out base properties
      if (stateData.element.properties) {
        result.properties = {...element.properties, ...stateData.element.properties};
      }
      return result;
    }

    return element;
  }

  private applyElementToShape(shape: Konva.Rect, element: OverlayElement): void {
    const zoom = this.cy?.zoom() || 1;
    const pan = this.cy?.pan() || {x: 0, y: 0};
    const props = element.properties || {};

    const rendered = this.toRendered(element.x || 0, element.y || 0);

    shape.x(rendered.x);
    shape.y(rendered.y);
    shape.width((element.width || 100) * zoom);
    shape.height((element.height || 100) * zoom);
    shape.rotation((element.rotation || 0) * (180 / Math.PI));
    shape.fill(props.backgroundColor || '#d3d3d3');
    shape.stroke(props.borderColor || '#000000');
    shape.strokeWidth((props.borderWidth ?? 2) * zoom);
    shape.opacity(props.opacity ?? 1);
    shape.dash(this.getDashArray(props.borderStyle, zoom) || []);
  }

  private toRendered(modelX: number, modelY: number): { x: number; y: number } {
    const zoom = this.cy?.zoom() || 1;
    const pan = this.cy?.pan() || {x: 0, y: 0};
    return {
      x: modelX * zoom + pan.x,
      y: modelY * zoom + pan.y
    };
  }

  private toModel(renderedX: number, renderedY: number): { x: number; y: number } {
    const zoom = this.cy?.zoom() || 1;
    const pan = this.cy?.pan() || {x: 0, y: 0};
    return {
      x: (renderedX - pan.x) / zoom,
      y: (renderedY - pan.y) / zoom
    };
  }

  private disableGestures(): void {
    if (!this.cy) return;
    this.lastPanningEnabled = this.cy.panningEnabled();
    this.lastZoomingEnabled = this.cy.zoomingEnabled();
    this.lastBoxSelectionEnabled = this.cy.boxSelectionEnabled();
    this.cy.panningEnabled(false);
    this.cy.zoomingEnabled(false);
    this.cy.boxSelectionEnabled(false);
  }

  private resetGestures(): void {
    if (!this.cy) return;
    this.cy.panningEnabled(this.lastPanningEnabled);
    this.cy.zoomingEnabled(this.lastZoomingEnabled);
    this.cy.boxSelectionEnabled(this.lastBoxSelectionEnabled);
  }

  private getDashArray(borderStyle?: string, zoom = 1): number[] | undefined {
    if (borderStyle === 'dashed') return [10 * zoom, 5 * zoom];
    if (borderStyle === 'dotted') return [2 * zoom, 4 * zoom];
    return undefined;
  }
}
